import React, { useEffect, useState } from "react";
import { GenerateView } from ".";
import { IFrameCommManager } from "../Managers";
import { Loading } from "../Components";
import { UserAnalytics } from "@metricell/user-analytics";

type ListeningState = "Listening" | "NotListening";

interface IViewShare {
    viewShareId: string;
    viewName: string;
    keyWords: string;
}

export interface IViewShareData {
    applicationName: string;
    creationUtcTime: string;
    id: string;
    keyWords: string;
    moduleId: number;
    toolUrlExtension: string;
    userId: string;
    viewShareName: string;
    author: string;
}

export interface IViewShareFolderData {
    toolId: number;
    displayName: string;
}

export const ViewShare: React.FunctionComponent = (): React.ReactElement => {
    const [viewShareId, setViewShareId] = useState<string>(null),
        [recievedLink, setRecievedLink] = useState<string>(null),
        [toolModuleId, setToolModuleId] = useState<number>(null),
        [generateToolModuleId, setGenerateToolModuleId] =
            useState<number>(null),
        [messageListeningState, setMessageListeningState] =
            useState<ListeningState>(null),
        [viewName, setViewName] = useState<string>(""),
        [keyWords, setKeyWords] = useState<string>(""),
        [tags, setTags] = useState([]),
        [viewShareList, setViewShareList] = useState<IViewShareData[]>([]),
        [isLoading, setIsLoading] = useState<boolean>(true),
        [viewShare, setViewShare] = useState<IViewShareData>(null);

    const key = `ViewShareWindow`;

    const viewshareOutput: string = viewShareId
        ? `${window.location.origin}/ViewShare-${viewShareId}`
        : ``;

    // eslint-disable-next-line arrow-body-style
    useEffect(() => {
        setIsLoading(true);
        void loadViewShares();
        setIsLoading(false);
        return (): void => {
            IFrameCommManager.deRegisterWatcher(key);
        };
    }, []);

    useEffect(() => IFrameCommManager.deRegisterWatcher(key), []);

    useEffect(() => {
        if (recievedLink !== null && generateToolModuleId !== null) {
            void createLink();
        }
    }, [recievedLink, generateToolModuleId]);

    const loadViewShares = async (): Promise<void> => {
        const result: string = await $.ajax({
            data: {
                moduleId: toolModuleId,
                keyWords: keyWords,
            },
            method: `GET`,
            url: `/ViewShare/LoadViewShareLibrary`,
        });

        setViewShareList(JSON.parse(result));
    };

    const requestLink = (): void => {
        setMessageListeningState(`Listening`);
        IFrameCommManager.messageTool(`Link?`);
        setTimeout(onNoLink, 3000);
    };

    const linkReceived = (moduleId: number, message: string): void => {
        setMessageListeningState(`NotListening`);
        setRecievedLink(message);
        setGenerateToolModuleId(moduleId);
    };

    const onNoLink = (): void => {
        // TODO: replace this function with noop
        // Do nothing
    };

    const onMessageReceived = (moduleId: number, message: string): void => {
        if (messageListeningState === `Listening`) {
            if (message) {
                linkReceived(moduleId, message);
            }
        }
    };

    /*
     * Create a view share link
     */
    const createLink = async (): Promise<void> => {
        const createViewShareLink: number = UserAnalytics.onAction(
            `create_view`,
            `viewshare`,
            `creating id`,
        );

        try {
            const resultText: string = await $.ajax({
                data: {
                    extension: recievedLink,
                    moduleId: generateToolModuleId,
                    viewShareName: viewName,
                    keyWords: tags.join(),
                },
                method: `POST`,
                url: `/ViewShare/NewViewShare`,
            });

            const resultObj: IViewShare = JSON.parse(resultText);

            UserAnalytics.onActionSuccess(createViewShareLink);
            setViewShareId(resultObj.viewShareId);

            setViewName(``);
            setKeyWords(``);
            setToolModuleId(null);
            setTags([]);

            await loadViewShares();
        } catch (e) {
            UserAnalytics.onActionError(createViewShareLink);
            throw new Error(`Create Link: ${e}`);
        }
    };

    /**
     * Delete a view share link
     * @param id = the id of the view share link
     */
    const deleteLink = async (id: string): Promise<void> => {
        const removeViewShareLink: number = UserAnalytics.onAction(
            `remove_view`,
            `viewshare`,
            `id: ${id}`,
        );

        const view = viewShareList.filter((v) => v.id === id);

        try {
            await $.ajax({
                data: {
                    viewShareId: id,
                },
                method: `POST`,
                url: `/ViewShare/DeleteViewShare`,
            });

            UserAnalytics.onActionSuccess(removeViewShareLink);
            void loadViewShares();
            setViewShare(view[0]);
        } catch (e) {
            UserAnalytics.onActionError(removeViewShareLink);
            throw new Error(`Delete View Share Link: ${e}`);
        }
    };

    /**
     * Delete a view share link
     * @param id = the id of the view share link
     */
    const revertDeleteLink = async (id: string): Promise<void> => {
        try {
            await $.ajax({
                data: {
                    viewShareId: id,
                },
                method: `POST`,
                url: `/ViewShare/RevertDeleteViewShare`,
            });
            void loadViewShares();
        } catch (e) {
            throw new Error(`Reverting Delete View Share Link: ${e}`);
        }
    };

    const shareLink = async (id: string): Promise<void> => {
        try {
            const resultText: string = await $.ajax({
                data: {
                    viewShareId: id,
                },
                method: `GET`,
                url: `/ViewShare/GetViewShareLink`,
            });

            const resultObj: IViewShare = JSON.parse(resultText);

            window.open(
                `${window.location.origin}/ViewShare-${resultObj.viewShareId}`,
                "_blank",
            );
        } catch (e) {
            throw new Error(` Share Link: ${e}`);
        }
    };

    // Setup the channel for the viewshare messages.
    IFrameCommManager.registerOrUpdateWatcher(key, onMessageReceived);

    if (isLoading) {
        return (
            <div className={`viewshare`}>
                <Loading />
            </div>
        );
    }

    return (
        <GenerateView
            deleteLink={deleteLink}
            getLink={requestLink}
            getList={loadViewShares}
            keyWords={keyWords}
            link={viewshareOutput}
            revertDeleteLink={revertDeleteLink}
            setKeyWords={setKeyWords}
            setTags={setTags}
            setViewName={setViewName}
            setViewShare={setViewShare}
            shareLink={shareLink}
            tags={tags}
            viewName={viewName}
            viewShare={viewShare}
            viewShareId={viewShareId}
            viewShareList={viewShareList}
        />
    );
};
